/* eslint-disable react/no-array-index-key */
import {useGetBasicDataItems} from '@/modules/basic-data/api'
import {mapBasicDataItemsToSelectItems} from '@/modules/basic-data/functions'
import {isTypeActiveSubstance, isTypeSolution, mapDrugItemsToSelectItems} from '@/modules/drug-catalog/functions'
import {useGetSolutionDrugs} from '@/modules/drug-catalog/solutions/api'
import {StabilityRuleCard} from '@/modules/drug-catalog/stability-rules/components'
import {StabilityRuleFormSchema} from '@/modules/drug-catalog/stability-rules/schemas'
import {useSelectedDrugItem} from '@/modules/products/trade-names/components/trade-name-form/utils'
import {volumeUnitEndElement} from '@/modules/products/trade-names/components/trade-name-form/volume-unit'
import {getTradeNameFormDefaultValues} from '@/modules/products/trade-names/functions'
import {useGetUnits} from '@/modules/products/units/api'
import {mapUnitsToSelectItems} from '@/modules/products/units/functions'
import {Alert, Box, Button, ButtonGroup, HStack, IconButton} from '@chakra-ui/react'
import {CatalogType} from '@cypro/engine/database/schema'
import {BasicDataResources} from '@cypro/engine/schema'
import {SolutionType} from '@cypro/engine/queries'
import {FormGrid, Tooltip} from '@cypro/ui/components'
import {FormSection} from '@cypro/ui/components/form/form-section'
import {EmptyState} from '@cypro/ui/components/ui/empty-state'
import {withForm} from '@cypro/ui/integrations/form'
import {useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'
import {LuPlus, LuTrash2} from 'react-icons/lu'

export const TradeNameStabilityRulesTab = withForm({
  defaultValues: getTradeNameFormDefaultValues(undefined),
  props: {
    missingStabilityStages: [] as string[],
    onAssignStabilityRule: () => {},
    onAddStabilityRule: (_index: number | undefined) => {},
    onEditStabilityRule: (_rule: StabilityRuleFormSchema, _index: number) => {},
    onDeleteStabilityRule: (_index: number) => {},
  },
  render: function Render({
    form,
    missingStabilityStages,
    onAssignStabilityRule,
    onAddStabilityRule,
    onEditStabilityRule,
    onDeleteStabilityRule,
  }) {
    const {t} = useTranslate('common')
    const {t: tTradeNames} = useTranslate('trade-names')

    const type = useStore(form.store, state => state.values.type)
    const drugCatalogItemId = useStore(form.store, state => state.values.drugCatalogItemId)
    const medicationProfileId = useStore(form.store, state => state.values.medicationProfileId)
    const stabilityRules = useStore(form.store, state => state.values.stabilityRules)
    const [drug] = useSelectedDrugItem(type, drugCatalogItemId)

    const medicationProfile = drug?.medicationProfiles?.find(p => p.id === medicationProfileId)
    const reconstitutionLockedByMedicationProfile = Boolean(medicationProfile?.reconstitutionRequired)

    const isActiveSubstance = isTypeActiveSubstance(type)
    const isSolution = isTypeSolution(type)
    const reconstitutionRequired = useStore(form.store, state => {
      if (state.values.type === CatalogType.enum.ACTIVE_SUBSTANCE) return state.values.reconstitutionRequired
      return null
    })
    const reconstitutedConcentrationUnitError = useStore(
      form.store,
      state => state.fieldMeta.reconstitutedConcentrationUnitId?.errors?.[0]?.message
    )

    const {solutions} = useGetSolutionDrugs({filters: {types: [SolutionType.enum.SOLVENT]}})
    const {units: concentrationUnits} = useGetUnits({filters: {type: 'concentration'}})
    const {items: containerMaterials} = useGetBasicDataItems({resource: BasicDataResources.containerMaterials})
    const {items: containerTypes} = useGetBasicDataItems({resource: BasicDataResources.containerTypes})
    const solutionsSelectItems = mapDrugItemsToSelectItems(solutions)
    const concentrationUnitsSelectItems = mapUnitsToSelectItems(concentrationUnits)
    const containerMaterialsSelectItems = mapBasicDataItemsToSelectItems(containerMaterials)
    const containerTypesSelectItems = mapBasicDataItemsToSelectItems(containerTypes)

    return (
      <Box spaceY="8">
        {(isActiveSubstance || isSolution) && Boolean(drugCatalogItemId) && (
          <FormGrid>
            <form.AppField name="containerMaterialId" mode="value">
              {field => (
                <field.Select
                  size="sm"
                  label={t('container_material')}
                  placeholder={t('eg', {value: containerMaterialsSelectItems?.[0]?.label})}
                  items={containerMaterialsSelectItems}
                />
              )}
            </form.AppField>

            <form.AppField name="containerTypeId" mode="value">
              {field => (
                <field.Select
                  size="sm"
                  label={t('container_type')}
                  placeholder={t('eg', {value: containerTypesSelectItems?.[0]?.label})}
                  items={containerTypesSelectItems}
                />
              )}
            </form.AppField>
          </FormGrid>
        )}

        {missingStabilityStages.length > 0 && (
          <Alert.Root status="warning" size="sm" borderRadius="l2">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title fontSize="sm">{tTradeNames('missing_stability_rules')}</Alert.Title>
              <Alert.Description fontSize="xs">
                {missingStabilityStages.map(stage => t(stage)).join(', ')}
              </Alert.Description>
            </Alert.Content>
          </Alert.Root>
        )}

        <FormSection label={tTradeNames('stability_rules')} description={tTradeNames('stability_rules_description')}>
          {!stabilityRules.length && <EmptyState title={t('no_stability_rules_added_yet')} />}

          {Boolean(stabilityRules.length) && (
            <FormGrid gridTemplateColumns="1fr 1fr">
              {stabilityRules.map((rule, index) => (
                <StabilityRuleCard
                  key={index}
                  rule={rule}
                  onEdit={() => onEditStabilityRule(rule, index)}
                  onDelete={() => onDeleteStabilityRule(index)}
                />
              ))}
            </FormGrid>
          )}

          <ButtonGroup size="sm" variant="ghost" colorPalette="cyan">
            <Button onClick={onAssignStabilityRule}>
              <LuPlus />
              {t('assign_rule')}
            </Button>

            <Button onClick={() => onAddStabilityRule(undefined)}>
              <LuPlus />
              {t('add_rule')}
            </Button>
          </ButtonGroup>
        </FormSection>

        {isActiveSubstance && (
          <FormSection label={t('reconstitution')}>
            <form.AppField name="reconstitutionRequired">
              {field => (
                <Tooltip
                  content={
                    reconstitutionLockedByMedicationProfile
                      ? tTradeNames('reconstitution_required_by_medicine_form')
                      : null
                  }
                >
                  <field.Switch
                    size="sm"
                    label={t('reconstitution_required')}
                    disabled={reconstitutionLockedByMedicationProfile}
                  />
                </Tooltip>
              )}
            </form.AppField>

            <HStack alignItems="flex-start" gapX="4" whiteSpace="nowrap">
              {reconstitutionRequired && (
                <FormGrid repeatCount={2} maxColumnWidth="20rem">
                  <form.AppField name="reconstitutedSolutionConcentration" mode="value">
                    {subField => (
                      <subField.NumberInput
                        size="sm"
                        label={t('concentration_of_reconstituted_solution')}
                        placeholder={tTradeNames('concentration_of_reconstituted_solution_placeholder')}
                        min={0}
                        required
                        hideControls
                        errorMessage={reconstitutedConcentrationUnitError}
                        inputGroupProps={{
                          endElement: (
                            <form.AppField name="reconstitutedConcentrationUnitId" mode="value">
                              {subField => (
                                <subField.Select
                                  label={t('reconstituted_concentration_unit')}
                                  placeholder={t('eg', {value: concentrationUnitsSelectItems?.[0]?.label})}
                                  items={concentrationUnitsSelectItems}
                                  required
                                  size="xs"
                                  hideLabel
                                  variant="ghost"
                                  controlProps={{minWidth: '20'}}
                                  hideErrorMessage
                                />
                              )}
                            </form.AppField>
                          ),
                        }}
                      />
                    )}
                  </form.AppField>

                  <form.AppField name="reconstitutedSolutionVolumeExpansion" mode="value">
                    {subField => (
                      <subField.NumberInput
                        size="sm"
                        label={t('volume_expansion_of_reconstituted_solution')}
                        placeholder={tTradeNames('reconstituted_solution_volume_expansion_placeholder')}
                        min={0}
                        required
                        inputGroupProps={{endElement: volumeUnitEndElement}}
                      />
                    )}
                  </form.AppField>
                </FormGrid>
              )}

              {reconstitutionRequired && (
                <form.Field name="reconstitutionSolvents" mode="array">
                  {field => (
                    <Box spaceY="4">
                      {field.state.value?.map((_, index) => {
                        const filteredItems = solutionsSelectItems.filter(item => {
                          const isSelectedInAnotherRow = field.state?.value?.some(
                            (val, i) => val.id === item.value && i !== index
                          )
                          return !isSelectedInAnotherRow
                        })

                        return (
                          <FormGrid key={index} repeatCount={3} maxColumnWidth="20rem">
                            <form.AppField name={`reconstitutionSolvents[${index}].id`} mode="value">
                              {subField => (
                                <subField.Select
                                  size="sm"
                                  label={t('reconstitution_solvent')}
                                  items={filteredItems}
                                  placeholder={t('eg', {value: solutionsSelectItems?.[0]?.label})}
                                  portalled={false}
                                  required
                                />
                              )}
                            </form.AppField>

                            <form.AppField name={`reconstitutionSolvents[${index}].solventVolume`} mode="value">
                              {subField => (
                                <subField.NumberInput
                                  size="sm"
                                  label={t('reconstitution_solvent_volume')}
                                  placeholder={tTradeNames('reconstitution_solvent_volume_placeholder')}
                                  min={0}
                                  required
                                  inputGroupProps={{endElement: volumeUnitEndElement}}
                                />
                              )}
                            </form.AppField>

                            {index !== 0 && (
                              <IconButton
                                variant="ghost"
                                colorPalette="red"
                                onClick={() => field.removeValue(index)}
                                aria-label={t('remove')}
                                width="max-content"
                                mt="5"
                              >
                                <LuTrash2 />
                              </IconButton>
                            )}

                            {field.state.value && field.state.value?.length <= 1 && (
                              <IconButton
                                variant="ghost"
                                colorPalette="cyan"
                                onClick={() => field.pushValue({id: '', solventVolume: 0})}
                                aria-label={t('add_new')}
                                width="max-content"
                                mt="5"
                              >
                                <LuPlus />
                              </IconButton>
                            )}
                          </FormGrid>
                        )
                      })}
                    </Box>
                  )}
                </form.Field>
              )}
            </HStack>
          </FormSection>
        )}
      </Box>
    )
  },
})
